import type { ApiHelper, ApiResponseWithData, BaseRequest } from '..';
import { ApiHelperWithData, splitParams } from '..';
import type {
  MstMemberCreateParams,
  MstMemberDetailResponse,
  MstMemberDialogItem,
  MstMemberDuplicateCheckParams,
  MstMemberDuplicateCheckResponse,
  MstMemberCareerGradeRecalculateParams,
  MstMemberCareerGradeRecalculateResponse,
  MstMemberMultiInsertParams,
  MstMemberUpdateParams,
  MstSearchBasicMembers,
  EmployeeNumberDuplicate,
} from '../model';
import type { MstMemberSearchItem, MstMemberSearchParams, PagerData } from '../model';

export class MstMemberApi {
  private withData: ApiHelperWithData;
  constructor(private helper: ApiHelper) {
    this.withData = new ApiHelperWithData(helper);
  }

  /**
   * 인력정보를 등록한다.
   */
  create = (
    params: MstMemberCreateParams & BaseRequest,
  ): Promise<ApiResponseWithData<{ result: number }>> => {
    const url = '/api/mst-member/create';
    return this.withData.postJson(url, ...splitParams(params));
  };

  /**
   * 인력정보를 일괄 등록한다.
   */
  multiInsert = (
    params: MstMemberMultiInsertParams & BaseRequest,
  ): Promise<ApiResponseWithData<{ result: number }>> => {
    const url = '/api/mst-member/multiInsert';
    return this.withData.postJson(url, ...splitParams(params));
  };

  /**
   * 인력정보를 수정한다.
   */
  update = (
    params: MstMemberUpdateParams & BaseRequest,
  ): Promise<ApiResponseWithData<{ result: number }>> => {
    const url = '/api/mst-member/update';
    return this.withData.postJson(url, ...splitParams(params));
  };

  delete = (
    params: { empId: string } & BaseRequest,
  ): Promise<ApiResponseWithData<number | { result: number }>> => {
    const url = '/api/mst-member/delete';
    return this.withData.postJson(url, ...splitParams(params));
  };

  checkDuplicate = (
    params: MstMemberDuplicateCheckParams & BaseRequest,
  ): Promise<ApiResponseWithData<MstMemberDuplicateCheckResponse>> => {
    const url = '/api/mst-member/check-duplicate';
    return this.withData.postJson(url, ...splitParams(params));
  };

  /**
   *  인력 검색 다이얼로그의 목록을 조회한다.
   *  PersonSearchDialog에서 사용한다.
   */
  dialogList = (
    params: { keyword?: string } & BaseRequest,
  ): Promise<ApiResponseWithData<{ list: MstMemberDialogItem[] }>> => {
    const url = '/apis/member/dialog-list';
    return this.withData.postJson(url, ...splitParams(params));
  };

  /*
   * 인력정보를 조회한다.
   */
  search = (
    params: MstMemberSearchParams & BaseRequest,
  ): Promise<
    ApiResponseWithData<{ pagerData?: PagerData<MstMemberSearchItem> | MstMemberSearchItem[] }>
  > => {
    const url = '/api/mst-member/search';
    return this.withData.postJson(url, ...splitParams(params));
  };

  /**
   * 인력정보 상세정보를 가져온다.
   */
  detail = (
    params: { empId: string } & BaseRequest,
  ): Promise<ApiResponseWithData<{ detail: MstMemberDetailResponse }>> => {
    const url = '/api/mst-member/detail';
    return this.withData.postJson(url, ...splitParams(params));
  };

  /**
   *  인력 검색 다이얼로그의 목록을 조회한다.
   *  MstMemberSelectDialog에서 사용한다.
   */
  searchBasicMembers = (
    params: BaseRequest = {},
  ): Promise<ApiResponseWithData<{ list: MstSearchBasicMembers[] }>> => {
    const url = '/apis/member/search-basic-members';
    return this.withData.postJson(url, ...splitParams(params));
  };

  /**
   *  인력 검색 다이얼로그의 목록을 조회한다.
   *  MstMemberSelectDialog에서 사용한다.
   */
  searchAllMembers = (
    params: BaseRequest = {},
  ): Promise<ApiResponseWithData<{ list: MstSearchBasicMembers[] }>> => {
    const url = '/apis/member/search-all-members';
    return this.withData.postJson(url, ...splitParams(params));
  };

  previewByName = (
    params: { nm: string } & BaseRequest,
  ): Promise<ApiResponseWithData<{ list: MstSearchBasicMembers[] }>> => {
    const url = '/apis/member/search-member-preview';

    return this.withData.postJson(url, ...splitParams(params));
  };

  checkEmployeeNumbers = (
    params: { empNums: string[] } & BaseRequest,
  ): Promise<ApiResponseWithData<{ list: EmployeeNumberDuplicate[] }>> => {
    const url = '/apis/member/check-employee-numbers';

    return this.withData.postJson(url, ...splitParams(params));
  };

  /**
   * 인력 총경력과 등급을 현재 산정 규칙으로 점검하거나 보정한다.
   */
  recalculateCareerGrade = (
    params: MstMemberCareerGradeRecalculateParams & BaseRequest,
  ): Promise<ApiResponseWithData<MstMemberCareerGradeRecalculateResponse>> => {
    const url = '/api/mst-member/career-grade/recalculate';
    return this.withData.postJson(url, ...splitParams(params));
  };
}
